import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/api';
import GuidedTour, { GuidedTourHelpButton } from '../onboarding/GuidedTour';
import SidebarDesktop from './SidebarDesktop';
import HeaderBar from './HeaderBar';
import MobileDrawer from './MobileDrawer';
import MobileBottomNav from './MobileBottomNav';
import { getNavItems, getMobileTabItems } from './navConfig';
import OfflineBanner from '../offline/OfflineBanner';
import { NPSWidget } from '../shared/NPSWidget';

function MainLayout() {
    const { user, logout, isAdmin, isAgent, isSeller, isSuperAdmin, isSoloAgent } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mandateCount, setMandateCount] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [notifCounts, setNotifCounts] = useState({ urgent: 0, important: 0, info: 0, total: 0 });

    // Fetch notification counts (urgent / important / info) for the bell badge
    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get('/notifications/counts');
                if (!cancelled && data) setNotifCounts(data);
            } catch {
                /* silent */
            }
        };
        load();
        const interval = setInterval(load, 60000); // refresh every 60s
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [user]);

    useEffect(() => {
        if (user && (isAdmin || isAgent)) {
            api
                .get('/mandates-v2/')
                .then((r) => {
                    const active = (r.data.mandates || []).filter((m) =>
                        ['actif', 'offre', 'sent', 'acknowledged', 'draft', 'active', 'generated'].includes(
                            m.status
                        )
                    );
                    setMandateCount(active.length);
                })
                .catch(() => {});
        }
    }, [user, isAdmin, isAgent]);

    // Poll unread messages count
    useEffect(() => {
        if (!user) return undefined;
        const fetchUnread = () => {
            import('../../lib/api').then(({ messagingAPI }) => {
                messagingAPI
                    .getUnreadCount()
                    .then((r) => {
                        setUnreadMessages(r.data.unread_count || 0);
                    })
                    .catch(() => {});
            });
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 15000);
        return () => clearInterval(interval);
    }, [user]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = getNavItems({ isSuperAdmin, isAdmin, isAgent, isSeller, isSoloAgent });
    const mobileTabs = getMobileTabItems({ isAdmin, isAgent, isSeller });
    const basePath = isSuperAdmin ? '/super-admin' : isAdmin ? '/admin' : isAgent ? '/agent' : '/seller';

    return (
        <div className="min-h-screen bg-background">
            {/* Background effects */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div
                    className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-[120px] ${
                        isDark ? 'bg-purple-500/8' : 'bg-purple-500/5'
                    }`}
                />
                <div
                    className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-[120px] ${
                        isDark ? 'bg-orange-500/8' : 'bg-orange-500/5'
                    }`}
                />
            </div>

            {/* Sidebar - Desktop */}
            <SidebarDesktop
                navItems={navItems}
                user={user}
                basePath={basePath}
                mandateCount={mandateCount}
                unreadMessages={unreadMessages}
            />

            {/* Main content */}
            <div className="md:ml-64 min-h-screen flex flex-col relative">
                <OfflineBanner />
                <HeaderBar
                    user={user}
                    isDark={isDark}
                    isAdmin={isAdmin}
                    basePath={basePath}
                    notifCounts={notifCounts}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    onLogout={handleLogout}
                />

                <MobileDrawer
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    navItems={navItems}
                    basePath={basePath}
                    user={user}
                />

                {/* Page content */}
                <main className="flex-1 p-3 sm:p-4 md:p-6 pb-24 md:pb-6 min-w-0">
                    <Outlet />
                </main>

                <MobileBottomNav
                    tabs={mobileTabs}
                    basePath={basePath}
                    isDark={isDark}
                    unreadMessages={unreadMessages}
                />
            </div>

            {/* Global guided tour & help button (mounted once for the whole app) */}
            {user?.role && (
                <>
                    <GuidedTour role={user.role === 'super_admin' ? 'admin' : user.role} />
                    <GuidedTourHelpButton />
                    <NPSWidget />
                </>
            )}
        </div>
    );
}

export default MainLayout;
